import { useState, useEffect } from 'react';
import { getDatabase, ref, onValue, query, orderByChild, equalTo, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';

// Hook für einzelne Event-Daten (von Products mit type='event')
export const useEventData = (eventId, forceRefresh = 0) => {
    const [eventData, setEventData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        console.log('🚀 useEventData - Effect triggered for eventId:', eventId);
        
        if (!eventId) {
            console.log('useEventData - No eventId provided, resetting state');
            setEventData(null);
            setLoading(false);
            setError(null);
            return;
        }

        // Reset states when eventId changes
        console.log('🔄 useEventData - Resetting states for:', eventId);
        setLoading(true);
        setError(null);
        setEventData(null);

        const db = getDatabase();
        const eventRef = ref(db, `products/${eventId}`);
        console.log('🎯 useEventData - Setting up NEW listener for:', eventRef.toString());

        // Force immediate read first, then listen for changes
        console.log('📖 useEventData - Forcing immediate read for:', eventId);
        
        get(eventRef).then((snapshot) => {
            console.log('⚡ useEventData - Immediate read completed for:', eventId);
            if (snapshot.exists()) {
                const rawData = snapshot.val();
                
                // Validate that this is actually an event
                if (rawData.type !== 'event') {
                    console.log('❌ useEventData - Product is not an event:', rawData.type);
                    setEventData(null);
                    setError('Selected product is not an event');
                    setLoading(false);
                    return;
                }
                
                const immediateData = { id: eventId, ...rawData };
                console.log('⚡ useEventData - Immediate event data:', immediateData);
                setEventData(immediateData);
                setError(null);
            } else {
                setEventData(null);
                setError('Event not found');
            }
            setLoading(false);
        }).catch((error) => {
            console.error('⚡ useEventData - Immediate read error:', error);
            setError('Failed to load event');
            setLoading(false);
        });

        const unsubscribe = onValue(eventRef, 
            (snapshot) => {
                console.log('🔥 useEventData - Firebase callback triggered for:', eventId, {
                    exists: snapshot.exists(),
                    timestamp: new Date().toISOString()
                });
                
                if (snapshot.exists()) {
                    const rawData = snapshot.val();
                    
                    // Validate that this is actually an event
                    if (rawData.type !== 'event') {
                        console.log('❌ useEventData - Product is not an event:', rawData.type);
                        setEventData(null);
                        setError('Selected product is not an event');
                        setLoading(false);
                        return;
                    }
                    
                    const newEventData = { id: eventId, ...rawData };
                    
                    console.log('✅ useEventData - Firebase event data received:', eventId);
                    console.log('📊 useEventData - Event content data:', {
                        eventType: newEventData.contentData?.eventType,
                        eventDaysCount: newEventData.contentData?.eventDays?.length || 0,
                        ticketsCount: newEventData.contentData?.tickets?.length || 0,
                        meetingUrl: newEventData.contentData?.meetingUrl,
                        eventAddress: newEventData.contentData?.eventAddress
                    });
                    
                    setEventData(newEventData);
                    setError(null);
                    console.log('✅ useEventData - State updated successfully for:', eventId);
                } else {
                    console.log('❌ useEventData - Event not found:', eventId);
                    setEventData(null);
                    setError('Event not found');
                }
                
                setLoading(false);
                console.log('🏁 useEventData - Loading set to false for:', eventId);
            },
            (error) => {
                console.error('useEventData - Firebase error for event:', eventId, error);
                setError('Failed to load event');
                setLoading(false);
            }
        );

        return () => {
            console.log('🧹 useEventData - Cleaning up listener for:', eventId);
            unsubscribe();
        };
    }, [eventId, forceRefresh]);

    return { eventData, loading, error };
};

// Hook für alle User-Events (Events sind Products mit type='event')
export const useUserEvents = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const auth = getAuth();
    const currentUser = auth.currentUser;

    useEffect(() => {
        if (!currentUser) {
            setEvents([]);
            setLoading(false);
            return;
        }

        const db = getDatabase();
        const productsQuery = query(
            ref(db, 'products'),
            orderByChild('providerId'),
            equalTo(currentUser.uid)
        );

        const unsubscribe = onValue(productsQuery,
            (snapshot) => {
                const eventsData = [];
                snapshot.forEach((childSnapshot) => {
                    const productData = childSnapshot.val();
                    // Nur Events hinzufügen
                    if (productData.type === 'event') {
                        eventsData.push({
                            id: childSnapshot.key,
                            ...productData
                        });
                    }
                });
                
                // Sortiere nach Erstellungsdatum (neueste zuerst)
                eventsData.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
                
                setEvents(eventsData);
                setError(null);
                setLoading(false);
            },
            (error) => {
                console.error('Error loading user events:', error);
                setError('Failed to load events');
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [currentUser]);

    return { events, loading, error };
};

// Helper function für Event-Status
export const getEventStatusInfo = (status) => {
    const statusConfig = {
        draft: { 
            label: 'Draft', 
            className: 'status-draft', 
            color: '#6b7280',
            icon: '📝' 
        },
        published: { 
            label: 'Published', 
            className: 'status-published', 
            color: '#10b981',
            icon: '✅' 
        },
        archived: { 
            label: 'Archived', 
            className: 'status-archived', 
            color: '#f59e0b',
            icon: '📦' 
        }
    };
    
    return statusConfig[status] || statusConfig.draft;
};

// Helper function für Event-Type Info
export const getEventTypeInfo = (eventType) => {
    const typeConfig = {
        digital: {
            label: 'Digital Event',
            icon: '💻',
            color: '#6366f1',
            description: 'Online event with meeting link'
        },
        'in-person': {
            label: 'In-Person Event',
            icon: '📍',
            color: '#f59e0b',
            description: 'Physical event at a location'
        }
    };
    
    return typeConfig[eventType] || typeConfig.digital;
};

// Helper function für Ticket-Price Formatting
export const formatTicketPrice = (price, currency = 'EUR') => {
    if (price === 0 || price === '0') {
        return 'Free';
    }
    
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: currency
    }).format(price);
};

// Helper function für Event-Date Formatting
export const formatEventDate = (dateString) => {
    if (!dateString) return 'TBD';
    
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('de-DE', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    } catch (error) {
        return dateString;
    }
};

// Helper function für Time Formatting
export const formatEventTime = (timeString) => {
    if (!timeString) return '';
    
    try {
        // Assume timeString is in format "HH:MM"
        return timeString;
    } catch (error) {
        return timeString;
    }
};
